import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Star, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * Lets any signed-in user submit a "Share Your Experience" testimonial
 * about RydeXpress (rating + comment). Goes to `/api/reviews/platform`
 * with status='pending' — admin must approve before it appears publicly.
 */
export default function ShareReviewDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!user) {
      toast.error("Please log in to share your experience.");
      onOpenChange(false);
      return;
    }
    if (!comment.trim()) {
      toast.error("Please write a quick comment.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews/platform", { rating, comment: comment.trim() });
      toast.success("Thanks! Your review is queued for admin approval.");
      setRating(5);
      setComment("");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="share-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Share your experience</DialogTitle>
          <DialogDescription>
            How was your time on RydeXpress? Your review is published after a quick admin check.
          </DialogDescription>
        </DialogHeader>

        {/* Star rating */}
        <div className="flex items-center justify-center gap-1.5 py-3" data-testid="share-review-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n)}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              data-testid={`share-review-star-${n}`}
              className="transition-transform hover:scale-110"
            >
              <Star
                size={32}
                className={(hover || rating) >= n
                  ? "fill-[#E97A48] text-[#E97A48]"
                  : "text-muted-foreground"}
              />
            </button>
          ))}
        </div>
        <div className="text-center text-sm text-muted-foreground -mt-1">
          {rating} of 5 — {rating === 5 ? "Loved it!" : rating === 4 ? "Great" : rating === 3 ? "OK" : rating === 2 ? "Below par" : "Disappointing"}
        </div>

        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Tell others what worked — booking flow, owners you met, the car…"
          rows={4}
          maxLength={500}
          data-testid="share-review-comment"
          className="resize-none"
        />
        <div className="text-[11px] text-muted-foreground text-right -mt-2">
          {comment.length}/500
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="share-review-cancel"
          >
            <X size={14} className="mr-1.5" /> Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="share-review-submit"
            className="bg-[#E97A48] hover:bg-[#D86A38] text-white"
          >
            {submitting ? "Sending…" : "Submit review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
